import validate from 'validate.js';
import axios from "axios";
import { ancestor } from '../element/ancestor';
import { mergeDeep } from '../object/merge-deep';

export default class Form {
	constructor(elem, options) {
		this.form = elem;
		this.errors = {};
		this.options = {
			constraints: {},
			form: {
				onValid: (form, instance) => {
					console.log(`Valid: ${new FormData(form)}`);
				},
			},
			input: {
				onValid: (input, instance) => {
					console.log(`${input.name} valid`);
				},
				onError: (input, instance) => {
					console.log(`${input.name} invalid`);
				},
			},
			error: {
				element: 'span',
				className: 'error',
			},
			classes: {
				form: {
					onSubmit: 'is-submitting',
					onSuccess: 'is-successful',
				},
				group: {
					onError: 'has-error',
				},
				label: {
					onError: 'has-error',
				},
				input: {
					onSuccess: 'has-success',
					onError: 'has-error',
				},
			},
			request: {
				url: null,
				type: 'post',
				submitDelay: 300,
			},
		};

		this.setOptions(options);
		this.initForm();
		this.initInputs();
	}

	setOptions(options) {
		this.options = mergeDeep(this.options, options);
	}

	initForm() {
		this.inputs = [...this.form.elements].filter((input) => {
			return input.name in this.options.constraints;
		});

		this.form.addEventListener('submit', (e) => {
			e.preventDefault();
			this.handleSubmit();
		});
	}

	initInputs() {
		this.inputs.forEach((input) => {
			input.group = this.initInputGroup(input);
			input.label = this.initInputLabel(input);

			input.addEventListener('change', (e) => {
				this.runValidation([input]);
			});
		});
	}

	initInputGroup(input) {
		return ancestor(input, this.options?.selectors?.inputGroup);
	}

	initInputLabel(input) {
		const label = input.group.querySelector(this.options?.selectors?.label);

		if (label !== null) {
			return label;
		} else {
			return false;
		}
	}

	runValidation(inputs) {
		inputs.forEach((input) => {
			let value;

			if (input.type === 'checkbox') {
				value = input.checked;
			} else {
				value = input.value;
			}

			const errors = validate.single(value, this.options.constraints[input.name]);

			if (errors !== undefined) {
				this.errors[input.name] = errors;
				input.hasErrors = true;
			} else {
				delete this.errors[input.name];
				input.hasErrors = false;
			}
			this.showErrorsForInput(input);
		});
	}

	handleSubmit() {
		this.runValidation(this.inputs);
		if (Object.entries(this.errors).length === 0) {
			this.valid();
		}
	}

	showErrorsForInput(input) {
		if (this.errors[input.name] !== undefined) {
			this.addError(input, this.errors[input.name]);
		} else {
			this.removeError(input);
		}
	}

	removeError(input) {
		input.removeAttribute('data-error');
		input.classList.remove(this.options.classes.input.onError);
		input.classList.add(this.options.classes.input.onSuccess);
		this.options?.input?.onValid(input, this);
		this.handleGroup(input.group, false);
		this.handleLabel(input.label, false);
	}

	addError(input, error) {
		input.dataset.error = error;
		input.classList.add(this.options.classes.input.onError);
		input.classList.remove(this.options.classes.input.onSuccess);
		this.options?.input?.onError(input, error, this);
		this.handleGroup(input.group, error);
		this.handleLabel(input.label, error);
	}

	handleGroup(group, error) {
		if (group) {
			if (error) {
				group.classList.add(this.options.classes?.group?.onError);
			} else {
				group.classList.remove(this.options.classes?.group?.onError);
			}
		}
	}

	handleLabel(label, error) {
		if (label) {
			if (error) {
				if (!this.hasErrorMessage(label)) {
					Object.assign(label, { errorMessage: this.createErrorMessage() });
					label.append(label.errorMessage);
				}
				this.updateErrorMessage(label.errorMessage, error);
			} else {
				this.removeErrorMessage(label);
				label.classList.remove(this.options.classes?.label?.onError);
			}
		}
	}

	hasErrorMessage(element) {
		let contains = false;
		[...element.children].forEach((child) => {
			if (child.matches(`${this.options?.error?.element}.${this.options?.error?.className}`)) {
				contains = true;
			}
		});
		return contains;
	}

	createErrorMessage() {
		return this.elementBuilder(this.options?.error?.element, {
			classList: this.options?.error?.className,
			innerText: '',
		});
	}

	updateErrorMessage(element, error) {
		element.innerText = error[0];
	}

	removeErrorMessage(label) {
		if (label.errorMessage) {
			label.removeChild(label.errorMessage);
			Object.assign(label, { errorMessage: false });
		}
	}

	elementBuilder(tag, options) {
		return Object.assign(document.createElement(tag), options);
	}

	valid() {
		if (this.options.request?.url !== null) {
			new Promise((resolve, reject) => {
				this.form.classList.add(this.options.classes.form.onSubmit);
				this.submitRequest(resolve, reject);
			}).then(() => {
				setTimeout(() => {
					this.form.classList.remove(this.options.classes.form.onSubmit);
				}, this.options.request.submitDelay);
			});
		}
		this.options?.form?.onValid(this.form, this);
	}

	submitRequest(resolve, reject) {
		axios.post(this.options.request.url, new FormData(this.form), {
			type: this.options.request.type,
		})
			.then((response) => {
				this.options.request.success
					? this.options.request.success(response, this)
					: console.log('Success :', response);
			})
			.catch((e) => {
				this.options.request?.error
					? this.options.request.error(e, this)
					: console.log('Error :', e);
				reject();
			})
			.finally(resolve);

	}
}
